/**
 * RetroPanel (S1/Stufe 2) — D-036 „Kennzeichnen statt Korrigieren".
 *
 * Dezente Anzeige der additiven D-036-Felder je Gesellschaft: Nachbuchungen
 * (retro_hinweise, 5a), Prüflücken (pruefluecken, 5c) und AfA-Plausibilität
 * (afa_warnung, 5d). Erklärung, KEIN Alarm — keine Ampel, kein Eingriff in
 * YTD/Zahlen. Fehlend/leer/null → nichts rendern (R5, SCHEMA_MAPPING §5).
 * Die Quartals-Grenz-Badges (5b) sitzen in AssetDetail (Soll/Ist-Sektion).
 */
import type { AfaWarnung, Pruefluecke, RetroHinweis } from '../types/snapshot';
import { formatEur } from '../lib/format';

export interface RetroPanelProps {
  retroHinweise?: RetroHinweis[];
  pruefluecken?: Pruefluecke[];
  afaWarnung?: AfaWarnung | null;
}

const ART_LABEL: Record<string, string> = {
  MONATSLUECKE: 'Monatslücke',
  ERSTER_STICHTAG: 'Erster Stichtag',
};

export function RetroPanel({ retroHinweise, pruefluecken, afaWarnung }: RetroPanelProps) {
  const hasRetro = (retroHinweise?.length ?? 0) > 0;
  const hasLuecken = (pruefluecken?.length ?? 0) > 0;
  const hasAfa = afaWarnung != null;
  if (!hasRetro && !hasLuecken && !hasAfa) return null;

  return (
    <section aria-label="D-036: Nachbuchungen & Prüfhinweise" className="space-y-3">
      <h3 className="text-lg font-semibold text-text">Nachbuchungen &amp; Prüfhinweise</h3>

      {hasRetro && (
        <div>
          <p className="mb-1 text-sm font-medium text-text-muted">
            Nachbuchungen aus Vorperioden (Erklärung, kein Alarm)
          </p>
          <ul className="space-y-0.5">
            {retroHinweise!.flatMap((h) =>
              Object.entries(h.kategorien).map(([kat, betrag]) => (
                <li key={`${h.monat}-${kat}`} className="text-xs text-text-muted">
                  Monat {h.monat} · {kat}: darin Nachbuchungen Vorperioden {formatEur(betrag)}
                </li>
              )),
            )}
            {retroHinweise!
              .filter((h) => h.nicht_zuordenbar > 0)
              .map((h) => (
                <li key={`nz-${h.monat}`} className="text-xs text-text-muted">
                  Monat {h.monat} · nicht zuordenbar: {formatEur(h.nicht_zuordenbar)}
                </li>
              ))}
          </ul>
        </div>
      )}

      {hasLuecken && (
        <div>
          <p className="mb-1 text-sm font-medium text-text-muted">Prüflücken</p>
          <ul className="space-y-0.5">
            {pruefluecken!.map((pl) => (
              <li
                key={`${pl.art}-${pl.monat}`}
                title={pl.grund ?? undefined}
                className={
                  pl.art === 'MONATSLUECKE'
                    ? 'text-xs font-medium text-text'
                    : 'text-xs text-text-muted'
                }
              >
                Monat {pl.monat} · <span>{pl.art ? (ART_LABEL[pl.art] ?? 'Prüflücke') : 'Prüflücke'}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasAfa && (
        <div
          className={
            afaWarnung!.stufe === 'WARNUNG'
              ? 'text-xs font-medium text-text'
              : 'text-xs text-text-muted'
          }
        >
          <span className="font-medium">
            {afaWarnung!.stufe === 'WARNUNG' ? 'AfA-Warnung' : 'AfA-Hinweis'}
          </span>
          <span>: {afaWarnung!.text}</span>
        </div>
      )}
    </section>
  );
}
